import * as THREE from 'three';
import { OBJLoader } from 'three/examples/jsm/loaders/OBJLoader.js';
import { CELL_SIZE, ROOM_COLS, ROOM_ROWS, OFFICE_ROOM_REGIONS, OFFICE_THRESHOLDS, OFFICE_CHAIR_LAYOUT } from './rooms/templates.js';

// Builds a single room's geometry + wall colliders + spawn points from a template.
// The room is centered on origin. Coordinates: world XZ, +X right, +Z down on the screen.

const FLOOR_COLORS = Object.freeze({
  lobby: 0x111111,
  openOffice: 0x111111,
  privateOffice: 0x111111,
  server: 0x111111,
  break: 0x111111,
  bathroomCorridor: 0x111111,
  hallway: 0x111111,
  default: 0x111111,
  threshold: 0x111111,
});
const FLOOR_ROUGHNESS = Object.freeze({
  lobby: 0.8,
  openOffice: 0.85,
  privateOffice: 0.85,
  server: 0.7,
  break: 0.8,
  bathroomCorridor: 0.9,
  hallway: 0.8,
  default: 0.85,
});
const WALL_HEIGHT = 4;
const FLOOR_THICKNESS = 0.1;
const CEILING_Y = 1.95;
const DOOR_BLOCKER_HALF_WIDTH = CELL_SIZE * 0.5;
const DOOR_BLOCKER_HALF_DEPTH = 0.25;
const WALL_SIDE_COLOR = 0x111111;
const WALL_TOP_COLOR  = 0x111111;
const WALL_EMISSIVE_COLOR = 0xff2d6b;
const WALL_EMISSIVE_INTENSITY = 0.12;
const WALL_RIM_COLOR = 0xff2d6b;
const WALL_RIM_EMISSIVE = 0xff2d6b;
const WALL_RIM_EMISSIVE_INTENSITY = 0.15;
const WALL_RIM_HEIGHT = 0.1;
const WALL_AO_WIDTH = 0.3;
const BOSS_COLS = 24;
const BOSS_ROWS = 36;
const STRUCTURAL_PREFIXES = ['pillar_', 'wall_'];
const PROP_COLLIDER_SHRINK = 0.65;
const CHAIR_OBJ_PATHS = Object.freeze([
  '/assets/models/Chairs/Office_Chair_Black_01.obj',
  '/assets/models/Chairs/Office_Chair_Black_02.obj',
  '/assets/models/Chairs/Office_Chair_Brown_01.obj',
]);
const _objLoader = new OBJLoader();
const _chairTemplateCache = new Map();
const _chairTemplatePromise = new Map();

function _loadChairTemplate(path) {
  if (_chairTemplateCache.has(path)) return Promise.resolve(_chairTemplateCache.get(path));
  if (_chairTemplatePromise.has(path)) return _chairTemplatePromise.get(path);
  const p = new Promise((resolve) => {
    _objLoader.load(
      path,
      (obj) => {
        _chairTemplateCache.set(path, obj);
        resolve(obj);
      },
      undefined,
      () => resolve(null),
    );
  });
  _chairTemplatePromise.set(path, p);
  return p;
}

export class Room {
  constructor({ scene, template, propTemplates = [] }) {
    this.scene = scene;
    this.template = template;
    this.rows = Array.isArray(template) ? template.length : ROOM_ROWS;
    this.cols = Array.isArray(template) && template[0] ? template[0].length : ROOM_COLS;
    this.isBossArena = this.rows === BOSS_ROWS && this.cols === BOSS_COLS && this.template.some((row) => row.includes('B'));
    this.group = new THREE.Group();
    this.walls = []; // {minX,minZ,maxX,maxZ}
    this.enemySpawns = [];
    this.propAnchors = [];
    this.playerSpawn = { x: 0, z: 0 };
    this.bossSpawn = null;
    this.door = null;
    this.doorOpen = false;
    this._navRevision = 0;
    this._propColliders = [];
    this._isDisposed = false;

    this._build(propTemplates);
    scene.add(this.group);
  }

  _cellToWorld(col, row) {
    const x = (col - (this.cols - 1) / 2) * CELL_SIZE;
    const z = (row - (this.rows - 1) / 2) * CELL_SIZE;
    return { x, z };
  }

  _build(propTemplates) {
    if (this.isBossArena) {
      this._buildBossArena(propTemplates);
      return;
    }
    this._buildFloors();
    const wallMat = new THREE.MeshStandardMaterial({
      color: WALL_SIDE_COLOR,
      emissive: WALL_EMISSIVE_COLOR,
      emissiveIntensity: WALL_EMISSIVE_INTENSITY,
      roughness: 0.9,
      metalness: 0.0,
    });
    const wallTopMat = new THREE.MeshStandardMaterial({
      color: WALL_TOP_COLOR,
      emissive: WALL_EMISSIVE_COLOR,
      emissiveIntensity: WALL_EMISSIVE_INTENSITY + 0.04,
      roughness: 0.88,
      metalness: 0.0,
    });
    this._buildWallsFromTemplate(wallMat, wallTopMat);
    this._scanSpawnAndDoorMarkers();
    this._paintDoorThresholds();
    this._buildOfficeProps(propTemplates);
    this._spawnTemplateChairs();
    this._buildCeilingLights();
  }

  _buildBossArena(propTemplates) {
    this._buildBossFloor();
    const wallMat = new THREE.MeshStandardMaterial({
      color: 0x111111,
      emissive: WALL_EMISSIVE_COLOR,
      emissiveIntensity: 0.12,
      roughness: 0.94,
      metalness: 0.01,
    });
    const wallTopMat = new THREE.MeshStandardMaterial({
      color: 0x111111,
      emissive: WALL_EMISSIVE_COLOR,
      emissiveIntensity: 0.14,
      roughness: 0.82,
      metalness: 0.06,
    });
    this._buildWallsFromTemplate(wallMat, wallTopMat);
    this._scanSpawnAndDoorMarkers();
    this._buildBossProps(propTemplates);
    this._buildBossLights();
    this._buildBossLaserAccent();
    this._lockBossEntranceVisual();
  }

  _buildFloors() {
    const floorGeo = new THREE.BoxGeometry(CELL_SIZE, FLOOR_THICKNESS, CELL_SIZE);
    const mats = new Map();
    const getFloorMat = (kind) => {
      if (!mats.has(kind)) {
        mats.set(kind, new THREE.MeshStandardMaterial({
          color: FLOOR_COLORS[kind] || FLOOR_COLORS.default,
          emissive: 0xff2d6b,
          emissiveIntensity: 0.1,
          roughness: FLOOR_ROUGHNESS[kind] ?? FLOOR_ROUGHNESS.default,
          metalness: 0.0,
        }));
      }
      return mats.get(kind);
    };
    for (let r = 0; r < this.rows; r++) {
      for (let c = 0; c < this.cols; c++) {
        const ch = this.template[r][c];
        if (ch === 'W') continue;
        const kind = this._regionKindForCell(c, r);
        const { x, z } = this._cellToWorld(c, r);
        const tile = new THREE.Mesh(floorGeo, getFloorMat(kind));
        tile.position.set(x, -FLOOR_THICKNESS * 0.5, z);
        tile.receiveShadow = true;
        this.group.add(tile);
      }
    }
    this._addFloorGridOverlay();
  }

  _addFloorGridOverlay() {
    const fullW = this.cols * CELL_SIZE;
    const fullH = this.rows * CELL_SIZE;
    const grid = new THREE.GridHelper(
      Math.max(fullW, fullH),
      Math.max(this.cols, this.rows),
      0x222222,
      0x202020,
    );
    grid.position.y = 0.01;
    if (Array.isArray(grid.material)) {
      for (const mat of grid.material) {
        mat.transparent = true;
        mat.opacity = 0.18;
      }
    } else if (grid.material) {
      grid.material.transparent = true;
      grid.material.opacity = 0.18;
    }
    this.group.add(grid);
  }

  _buildWallsFromTemplate(wallMat, wallTopMat) {
    const wallMaterials = [wallMat, wallMat, wallTopMat, wallMat, wallMat, wallMat];
    const rimMat = new THREE.MeshStandardMaterial({
      color: WALL_RIM_COLOR,
      emissive: WALL_RIM_EMISSIVE,
      emissiveIntensity: WALL_RIM_EMISSIVE_INTENSITY,
      roughness: 0.6,
      metalness: 0.05,
    });
    const aoMat = new THREE.MeshBasicMaterial({
      color: 0x000000,
      transparent: true,
      opacity: 0.5,
      depthWrite: false,
    });
    for (let r = 0; r < this.rows; r++) {
      for (let c = 0; c < this.cols; c++) {
        if (this.template[r][c] !== 'W') continue;
        const { x, z } = this._cellToWorld(c, r);
        const wall = new THREE.Mesh(new THREE.BoxGeometry(CELL_SIZE, WALL_HEIGHT, CELL_SIZE), wallMaterials);
        wall.position.set(x, WALL_HEIGHT * 0.5, z);
        wall.castShadow = true;
        wall.receiveShadow = true;
        this.group.add(wall);
        const rim = new THREE.Mesh(
          new THREE.BoxGeometry(CELL_SIZE, WALL_RIM_HEIGHT, CELL_SIZE),
          rimMat,
        );
        rim.position.set(x, WALL_HEIGHT + WALL_RIM_HEIGHT * 0.5, z);
        this.group.add(rim);
        const aoBand = new THREE.Mesh(
          new THREE.PlaneGeometry(CELL_SIZE + WALL_AO_WIDTH, CELL_SIZE + WALL_AO_WIDTH),
          aoMat,
        );
        aoBand.rotation.x = -Math.PI / 2;
        aoBand.position.set(x, 0.015, z);
        this.group.add(aoBand);
        this.walls.push({
          minX: x - CELL_SIZE * 0.5,
          maxX: x + CELL_SIZE * 0.5,
          minZ: z - CELL_SIZE * 0.5,
          maxZ: z + CELL_SIZE * 0.5,
        });
      }
    }
  }

  _scanSpawnAndDoorMarkers() {
    for (let r = 0; r < this.rows; r++) {
      for (let c = 0; c < this.cols; c++) {
        const cell = this.template[r][c];
        if (cell === 'S') this.playerSpawn = this._cellToWorld(c, r);
        else if (cell === 'E') this.enemySpawns.push(this._cellToWorld(c, r));
        else if (cell === 'B') this.bossSpawn = this._cellToWorld(c, r);
        else if (cell === 'D') this.door = { col: c, row: r, ...this._cellToWorld(c, r) };
      }
    }
  }

  _paintDoorThresholds() {
    if (this.isBossArena) return;
    const thresholdMat = new THREE.MeshStandardMaterial({
      color: FLOOR_COLORS.threshold,
      roughness: 0.92,
      metalness: 0,
      emissive: 0xff2d6b,
      emissiveIntensity: 0.12,
    });
    for (const t of OFFICE_THRESHOLDS) {
      if (this.template[t.row]?.[t.col] === 'W') continue;
      const { x, z } = this._cellToWorld(t.col, t.row);
      const strip = new THREE.Mesh(
        new THREE.BoxGeometry(CELL_SIZE, 0.04, CELL_SIZE),
        thresholdMat,
      );
      strip.position.set(x, 0.01, z);
      strip.receiveShadow = true;
      this.group.add(strip);
    }
    if (this.door) {
      this.door.blockerWall = {
        minX: this.door.x - DOOR_BLOCKER_HALF_WIDTH,
        maxX: this.door.x + DOOR_BLOCKER_HALF_WIDTH,
        minZ: this.door.z - DOOR_BLOCKER_HALF_DEPTH,
        maxZ: this.door.z + DOOR_BLOCKER_HALF_DEPTH,
      };
      this.walls.push(this.door.blockerWall);
    }
  }

  _regionKindForCell(col, row) {
    if (this.isBossArena) return 'default';
    for (const region of OFFICE_ROOM_REGIONS) {
      if (col >= region.minCol && col <= region.maxCol && row >= region.minRow && row <= region.maxRow) {
        return region.kind;
      }
    }
    return 'default';
  }

  _buildOfficeProps(propTemplates) {
    const hasKitbash = Array.isArray(propTemplates) && propTemplates.length > 0;
    const applyPropFlags = (mesh) => {
      mesh.castShadow = true;
      mesh.receiveShadow = true;
      mesh.userData.isProp = true;
    };
    const addBlocker = (x, z, w, d) => {
      const halfW = w * 0.5;
      const halfD = d * 0.5;
      const blocker = { minX: x - halfW, maxX: x + halfW, minZ: z - halfD, maxZ: z + halfD };
      this.walls.push(blocker);
      this._propColliders.push({
        x,
        z,
        width: w,
        depth: d,
        minX: blocker.minX,
        maxX: blocker.maxX,
        minZ: blocker.minZ,
        maxZ: blocker.maxZ,
      });
    };
    const shouldKeepFullCollider = (name) => {
      const lowered = String(name || '').toLowerCase();
      return STRUCTURAL_PREFIXES.some((prefix) => lowered.startsWith(prefix));
    };
    const makePropColliderFromBox = (box, { structural = false } = {}) => {
      const size = new THREE.Vector3();
      const center = new THREE.Vector3();
      box.getSize(size);
      box.getCenter(center);
      const scale = structural ? 1 : PROP_COLLIDER_SHRINK;
      const halfW = size.x * scale * 0.5;
      const halfD = size.z * scale * 0.5;
      const collider = {
        minX: center.x - halfW,
        maxX: center.x + halfW,
        minZ: center.z - halfD,
        maxZ: center.z + halfD,
      };
      this.walls.push(collider);
      this._propColliders.push({
        x: center.x,
        z: center.z,
        width: halfW * 2,
        depth: halfD * 2,
        minX: collider.minX,
        maxX: collider.maxX,
        minZ: collider.minZ,
        maxZ: collider.maxZ,
        structural,
      });
    };
    const addPrimitiveAtCell = (col, row, { w, h, d, color, roughness = 0.88, metalness = 0.02, emissive = 0x000000, emissiveIntensity = 0, yOffset = 0, blocker = false }) => {
      const { x, z } = this._cellToWorld(col, row);
      const mesh = new THREE.Mesh(
        new THREE.BoxGeometry(w, h, d),
        new THREE.MeshStandardMaterial({ color, roughness, metalness, emissive, emissiveIntensity }),
      );
      mesh.position.set(x, yOffset + h * 0.5, z);
      applyPropFlags(mesh);
      this.group.add(mesh);
      if (blocker) addBlocker(x, z, w, d);
      return mesh;
    };
    const addKitbashAtCell = (col, row, { blocker = false, rotY = 0 } = {}) => {
      if (!hasKitbash) return false;
      const { x, z } = this._cellToWorld(col, row);
      const src = propTemplates[(col + row) % propTemplates.length];
      const clone = src.clone ? src.clone(true) : src;
      clone.position.set(x, 0, z);
      clone.rotation.y = rotY;
      clone.traverse((n) => {
        if (!n.isMesh) return;
        applyPropFlags(n);
      });
      clone.userData.isProp = true;
      this.group.add(clone);
      if (blocker) {
        const box = new THREE.Box3().setFromObject(clone);
        const structural = shouldKeepFullCollider(src.name || clone.name || '');
        makePropColliderFromBox(box, { structural });
      }
      return true;
    };
    const addDeskSet = (col, row, { blocker = true } = {}) => {
      if (!addKitbashAtCell(col, row, { blocker })) {
        addPrimitiveAtCell(col, row, { w: 1.8, h: 0.7, d: 0.9, color: 0x3a3530, blocker });
      }
      addPrimitiveAtCell(col, row, {
        w: 0.5, h: 0.4, d: 0.05, color: 0x111111, emissive: 0xff2d6b, emissiveIntensity: 0.14, yOffset: 0.72, blocker: false,
      });
      const { x, z } = this._cellToWorld(col, row);
      const chair = new THREE.Mesh(
        new THREE.BoxGeometry(0.6, 0.6, 0.6),
        new THREE.MeshStandardMaterial({ color: 0x222222, roughness: 0.9, metalness: 0.01 }),
      );
      chair.position.set(x, 0.3, z + 0.7);
      applyPropFlags(chair);
      this.group.add(chair);
    };
    const addPlant = (col, row) => {
      const { x, z } = this._cellToWorld(col, row);
      const pot = new THREE.Mesh(
        new THREE.CylinderGeometry(0.18, 0.2, 0.25, 10),
        new THREE.MeshStandardMaterial({ color: 0x2a1a0a, roughness: 0.9 }),
      );
      pot.position.set(x, 0.125, z);
      applyPropFlags(pot);
      this.group.add(pot);
      const leaves = new THREE.Mesh(
        new THREE.SphereGeometry(0.28, 10, 8),
        new THREE.MeshStandardMaterial({ color: 0x1a3a1a, roughness: 0.85 }),
      );
      leaves.position.set(x, 0.45, z);
      applyPropFlags(leaves);
      this.group.add(leaves);
    };

    // OPEN OFFICE
    for (const [c, r] of [[2, 2], [3, 2], [2, 5], [3, 5], [5, 2], [6, 2]]) addDeskSet(c, r, { blocker: false });
    addPlant(1, 7);
    addPlant(7, 1);

    // PRIVATE OFFICE A
    addDeskSet(14, 4, { blocker: false });
    addPrimitiveAtCell(14, 5, { w: 0.6, h: 0.6, d: 0.6, color: 0x222222 });
    addPrimitiveAtCell(13, 4, { w: 0.3, h: 1.4, d: 1.8, color: 0x1a1510, blocker: false });

    // PRIVATE OFFICE B (mirrored)
    addDeskSet(11, 12, { blocker: false });
    addPrimitiveAtCell(11, 13, { w: 0.6, h: 0.6, d: 0.6, color: 0x222222 });
    addPrimitiveAtCell(10, 12, { w: 0.3, h: 1.4, d: 1.8, color: 0x1a1510, blocker: false });

    // SERVER ROOM
    for (const [c, r] of [[15, 11], [16, 11], [15, 13], [16, 13]]) {
      addPrimitiveAtCell(c, r, { w: 0.6, h: 1.5, d: 1.2, color: 0x0f1215, blocker: false });
      const { x, z } = this._cellToWorld(c, r);
      for (let i = 0; i < 4; i++) {
        const dot = new THREE.Mesh(
          new THREE.BoxGeometry(0.05, 0.05, 0.05),
          new THREE.MeshStandardMaterial({ color: 0x111111, emissive: 0xff2d6b, emissiveIntensity: 1.3 }),
        );
        dot.position.set(x - 0.22 + i * 0.14, 0.4 + (i % 2) * 0.25, z + 0.61);
        applyPropFlags(dot);
        this.group.add(dot);
      }
    }

    // BREAK ROOM
    addPrimitiveAtCell(16, 4, { w: 1.2, h: 0.7, d: 0.8, color: 0x2a2520, blocker: false });
    addPrimitiveAtCell(16, 3, { w: 0.6, h: 0.6, d: 0.6, color: 0x222222 });
    addPrimitiveAtCell(16, 5, { w: 0.6, h: 0.6, d: 0.6, color: 0x222222 });
    addPrimitiveAtCell(18, 2, { w: 0.3, h: 0.9, d: 1.8, color: 0x1a1a1a, blocker: false });
    addPrimitiveAtCell(17, 7, { w: 0.8, h: 1.5, d: 0.5, color: 0x111111, emissive: 0xff2d6b, emissiveIntensity: 0.14, blocker: false });

    // LOBBY
    addPrimitiveAtCell(4, 11, { w: 2.0, h: 0.8, d: 0.8, color: 0x1e1e1e, blocker: false });
    addPrimitiveAtCell(1, 14, { w: 0.7, h: 0.6, d: 0.7, color: 0x2a2020 });
    addPrimitiveAtCell(1, 16, { w: 0.7, h: 0.6, d: 0.7, color: 0x2a2020 });
    addPlant(7, 18);
  }

  _spawnTemplateChairs() {
    if (!Array.isArray(OFFICE_CHAIR_LAYOUT) || OFFICE_CHAIR_LAYOUT.length === 0) return;
    for (const anchor of OFFICE_CHAIR_LAYOUT) {
      if (!anchor) continue;
      const col = Number(anchor.col);
      const row = Number(anchor.row);
      if (!Number.isFinite(col) || !Number.isFinite(row)) continue;
      if (this.template[row]?.[col] === 'W') continue;
      const { x, z } = this._cellToWorld(col, row);
      this._spawnChairObjAt({
        x,
        z,
        rotY: Number(anchor.rotY) || 0,
        scale: Number(anchor.scale) || 0.95,
        variant: Number(anchor.variant) || 0,
      });
    }
  }

  _spawnChairObjAt({ x, z, rotY = 0, scale = 0.95, variant = 0 }) {
    const path = CHAIR_OBJ_PATHS[Math.abs(variant) % CHAIR_OBJ_PATHS.length];
    _loadChairTemplate(path).then((src) => {
      if (!src || this._isDisposed) return;
      const chair = src.clone(true);
      chair.position.set(x, 0, z);
      chair.rotation.y = rotY;
      chair.scale.setScalar(scale);
      chair.traverse((node) => {
        if (!node.isMesh) return;
        node.castShadow = true;
        node.receiveShadow = true;
        node.userData.isProp = true;
        if (node.material) {
          const mats = Array.isArray(node.material) ? node.material : [node.material];
          for (const m of mats) {
            m.side = THREE.DoubleSide;
          }
        }
      });
      const box = new THREE.Box3().setFromObject(chair);
      const size = new THREE.Vector3();
      box.getSize(size);
      // Normalize mixed OBJ authoring pivots/heights before final placement.
      const desiredHeight = 0.95;
      if (size.y > 0.001) {
        const correction = desiredHeight / size.y;
        chair.scale.multiplyScalar(correction);
      }
      const floorFixed = new THREE.Box3().setFromObject(chair);
      chair.position.y -= floorFixed.min.y;
      this.group.add(chair);
      const colliderBox = new THREE.Box3().setFromObject(chair);
      this._addPropColliderFromBox(colliderBox, { structural: false });
      this._navRevision++;
    });
  }

  _addPropColliderFromBox(box, { structural = false } = {}) {
    if (!box) return;
    const size = new THREE.Vector3();
    const center = new THREE.Vector3();
    box.getSize(size);
    box.getCenter(center);
    if (size.x <= 0.001 || size.z <= 0.001) return;
    const scale = structural ? 1 : PROP_COLLIDER_SHRINK;
    const halfW = size.x * scale * 0.5;
    const halfD = size.z * scale * 0.5;
    const collider = {
      minX: center.x - halfW,
      maxX: center.x + halfW,
      minZ: center.z - halfD,
      maxZ: center.z + halfD,
    };
    this.walls.push(collider);
    this._propColliders.push({
      x: center.x,
      z: center.z,
      width: halfW * 2,
      depth: halfD * 2,
      minX: collider.minX,
      maxX: collider.maxX,
      minZ: collider.minZ,
      maxZ: collider.maxZ,
      structural,
    });
  }

  getBounds() {
    const halfX = (this.cols * CELL_SIZE) / 2;
    const halfZ = (this.rows * CELL_SIZE) / 2;
    return { minX: -halfX, maxX: halfX, minZ: -halfZ, maxZ: halfZ };
  }

  getDoorPosition() {
    return this.door ? { x: this.door.x, z: this.door.z } : null;
  }

  getProps() {
    return this._propColliders.map((p) => ({ ...p }));
  }

  _buildCeilingLights() {
    if (this.isBossArena) return;
    const fixtureMatWarm = new THREE.MeshStandardMaterial({ color: 0x111111, emissive: 0xff2d6b, emissiveIntensity: 0.12 });
    const fixtureMatCool = new THREE.MeshStandardMaterial({ color: 0x111111, emissive: 0xff2d6b, emissiveIntensity: 0.13 });
    const fixtureMatLobby = new THREE.MeshStandardMaterial({ color: 0x111111, emissive: 0xff2d6b, emissiveIntensity: 0.15 });
    const addFixture = (x, z, { color = 0xff2d6b, intensity = 0.45, distance = 9, decay = 2, kind = 'warm' } = {}) => {
      const mat = kind === 'cool' ? fixtureMatCool : (kind === 'lobby' ? fixtureMatLobby : fixtureMatWarm);
      const fixture = new THREE.Mesh(new THREE.BoxGeometry(0.3, 0.05, 0.3), mat);
      fixture.position.set(x, CEILING_Y, z);
      this.group.add(fixture);
      const light = new THREE.PointLight(color, intensity, distance, decay);
      light.position.set(x, CEILING_Y - 0.1, z);
      light.castShadow = false;
      this.group.add(light);
    };
    for (const region of OFFICE_ROOM_REGIONS) {
      const colCenter = (region.minCol + region.maxCol) * 0.5;
      const rowCenter = (region.minRow + region.maxRow) * 0.5;
      if (region.kind === 'hallway' || region.kind === 'bathroomCorridor') {
        for (let row = region.minRow + 1; row <= region.maxRow; row += 3) {
          const { x, z } = this._cellToWorld(Math.round(colCenter), row);
          addFixture(x, z, { color: 0xff2d6b, intensity: 0.35, kind: 'warm' });
        }
        continue;
      }
      for (let row = region.minRow + 1; row <= region.maxRow; row += 4) {
        for (let col = region.minCol + 1; col <= region.maxCol; col += 4) {
          const { x, z } = this._cellToWorld(col, row);
          if (region.kind === 'server') addFixture(x, z, { color: 0xff2d6b, intensity: 0.5, kind: 'cool' });
          else if (region.kind === 'lobby') addFixture(x, z, { color: 0xff2d6b, intensity: 0.6, kind: 'lobby' });
          else addFixture(x, z, { color: 0xff2d6b, intensity: 0.45, kind: 'warm' });
        }
      }
      const { x: cx, z: cz } = this._cellToWorld(Math.round(colCenter), Math.round(rowCenter));
      addFixture(cx, cz, region.kind === 'server'
        ? { color: 0xff2d6b, intensity: 0.5, kind: 'cool' }
        : region.kind === 'lobby'
          ? { color: 0xff2d6b, intensity: 0.6, kind: 'lobby' }
          : { color: 0xff2d6b, intensity: 0.45, kind: 'warm' });
    }
    // Warm ambient fill so no surface goes pitch black
    this.group.add(new THREE.AmbientLight(0x080808, 0.32));
  }

  updatePlayerLightGlow(_player, dt = 0) {
    if (!this._doorGlow) return;
    const g = this._doorGlow;
    g.t = (g.t || 0) + (dt || 0.016) * 2.2;
    const pulse = 0.65 + 0.35 * Math.sin(g.t);
    g.mat.emissiveIntensity = 2.8 * pulse;
    g.light.intensity = 3.5 * pulse;
    g.arrow.position.y = 0.45 + 0.12 * Math.sin(g.t * 1.3);
  }

  _buildBossFloor() {
    const interiorW = (this.cols - 2) * CELL_SIZE;
    const interiorH = (this.rows - 2) * CELL_SIZE;
    const base = new THREE.Mesh(
      new THREE.PlaneGeometry(interiorW, interiorH),
      new THREE.MeshStandardMaterial({ color: 0x111111, emissive: 0xff2d6b, emissiveIntensity: 0.1, roughness: 0.98, metalness: 0 }),
    );
    base.rotation.x = -Math.PI / 2;
    base.position.y = 0;
    base.receiveShadow = true;
    this.group.add(base);

    const grid = new THREE.GridHelper(Math.max(interiorW, interiorH), Math.floor(Math.max(interiorW, interiorH) / 4), 0x252525, 0x252525);
    grid.position.y = 0.02;
    this.group.add(grid);

    const borderMat = new THREE.MeshStandardMaterial({ color: 0x111111, emissive: 0xff2d6b, emissiveIntensity: 0.1, roughness: 1.0, metalness: 0 });
    const borderThickness = CELL_SIZE;
    const addBorder = (w, d, x, z) => {
      const m = new THREE.Mesh(new THREE.BoxGeometry(w, 0.06, d), borderMat);
      m.position.set(x, 0.03, z);
      m.receiveShadow = true;
      this.group.add(m);
    };
    addBorder(interiorW, borderThickness, 0, -interiorH * 0.5 + borderThickness * 0.5);
    addBorder(interiorW, borderThickness, 0, interiorH * 0.5 - borderThickness * 0.5);
    addBorder(borderThickness, interiorH - 2 * borderThickness, -interiorW * 0.5 + borderThickness * 0.5, 0);
    addBorder(borderThickness, interiorH - 2 * borderThickness, interiorW * 0.5 - borderThickness * 0.5, 0);

    const runnerW = 3 * CELL_SIZE;
    const carpet = new THREE.Mesh(
      new THREE.PlaneGeometry(runnerW, interiorH - CELL_SIZE * 1.5),
      new THREE.MeshStandardMaterial({ color: 0x111111, emissive: 0xff2d6b, emissiveIntensity: 0.11, roughness: 0.9, metalness: 0 }),
    );
    carpet.rotation.x = -Math.PI / 2;
    carpet.position.set(0, 0.025, 0);
    carpet.receiveShadow = true;
    this.group.add(carpet);

    const platformCols = 6;
    const platformRows = 8;
    const centerCol = Math.floor(this.cols / 2);
    const startCol = centerCol - Math.floor(platformCols / 2);
    const startRow = 1;
    const platformW = platformCols * CELL_SIZE;
    const platformH = platformRows * CELL_SIZE;
    const platformCenter = this._cellToWorld(startCol + platformCols * 0.5 - 0.5, startRow + platformRows * 0.5 - 0.5);
    const platform = new THREE.Mesh(
      new THREE.BoxGeometry(platformW, 0.2, platformH),
      new THREE.MeshStandardMaterial({ color: 0x111111, emissive: 0xff2d6b, emissiveIntensity: 0.12, roughness: 0.95, metalness: 0 }),
    );
    platform.position.set(platformCenter.x, 0.1, platformCenter.z);
    platform.receiveShadow = true;
    this.group.add(platform);
  }

  _buildBossProps(propTemplates) {
    const hasKitbash = propTemplates && propTemplates.length > 0;
    const addPrim = ({ x, z, w, h, d, color = 0x111111, emissive = 0xff2d6b, blocker = false, ry = 0 }) => {
      const mesh = new THREE.Mesh(
        new THREE.BoxGeometry(w, h, d),
        new THREE.MeshStandardMaterial({ color, emissive, emissiveIntensity: emissive ? 0.12 : 0, roughness: 0.85, metalness: 0.08 }),
      );
      mesh.position.set(x, h * 0.5, z);
      mesh.rotation.y = ry;
      mesh.castShadow = true;
      mesh.receiveShadow = true;
      this.group.add(mesh);
      if (blocker) this.walls.push({ minX: x - w * 0.5, maxX: x + w * 0.5, minZ: z - d * 0.5, maxZ: z + d * 0.5 });
      return mesh;
    };
    const addAtCell = (c, r, cfg) => {
      const { x, z } = this._cellToWorld(c, r);
      if (hasKitbash && cfg.allowKitbash !== false) {
        const tpl = propTemplates[(c + r) % propTemplates.length];
        const clone = tpl.clone ? tpl.clone(true) : tpl;
        clone.position.set(x, 0, z);
        clone.rotation.y = cfg.ry || 0;
        clone.traverse((n) => { if (n.isMesh) { n.castShadow = true; n.receiveShadow = true; } });
        this.group.add(clone);
        if (cfg.blocker) {
          const box = new THREE.Box3().setFromObject(clone);
          this.walls.push({ minX: box.min.x, maxX: box.max.x, minZ: box.min.z, maxZ: box.max.z });
        }
        return;
      }
      addPrim({ ...cfg, x, z });
    };
    const mirrorX = (col) => this.cols - 1 - col;

    const c = Math.floor(this.cols / 2);
    addAtCell(c, 2, { w: 3.4, h: 1.3, d: 1.1, color: 0x3a2a2a, blocker: true, allowKitbash: false });
    addAtCell(c - 3, 3, { w: 0.9, h: 2.2, d: 0.9, color: 0x2f3642, blocker: true, allowKitbash: false });
    addAtCell(c + 3, 3, { w: 0.9, h: 2.2, d: 0.9, color: 0x2f3642, blocker: true, allowKitbash: false });
    addAtCell(c - 6, 4, { w: 0.9, h: 2.0, d: 0.9, color: 0x464646, blocker: false, allowKitbash: false });
    addAtCell(c + 6, 4, { w: 0.9, h: 2.0, d: 0.9, color: 0x464646, blocker: false, allowKitbash: false });

    // East/west mirrored lounge.
    for (const row of [8, 13, 18]) {
      addAtCell(3, row, { w: 2.2, h: 1.0, d: 0.95, color: 0x3b2b2b, ry: 0.2, blocker: false, allowKitbash: false });
      addAtCell(mirrorX(3), row, { w: 2.2, h: 1.0, d: 0.95, color: 0x3b2b2b, ry: -0.2, blocker: false, allowKitbash: false });
    }
    for (const row of [10, 16]) {
      addAtCell(2, row, { w: 1.2, h: 0.08, d: 1.8, color: 0x1d1d24, emissive: row % 2 ? 0x4488ff : 0xff8800, blocker: false, allowKitbash: false });
      addAtCell(mirrorX(2), row, { w: 1.2, h: 0.08, d: 1.8, color: 0x1d1d24, emissive: row % 2 ? 0xff8800 : 0x4488ff, blocker: false, allowKitbash: false });
    }

    // Mid-room side tables + sofa clusters.
    for (const col of [6, mirrorX(6)]) {
      addAtCell(col, 20, { w: 0.8, h: 0.65, d: 0.8, color: 0x444444, blocker: false, allowKitbash: false });
      addAtCell(col - 1, 23, { w: 1.8, h: 0.95, d: 0.9, color: 0x412f2f, blocker: false, allowKitbash: false });
      addAtCell(col + 1, 23, { w: 1.8, h: 0.95, d: 0.9, color: 0x412f2f, blocker: false, allowKitbash: false });
    }

    // South flanking entry props.
    addAtCell(c - 3, this.rows - 4, { w: 0.8, h: 1.1, d: 0.8, color: 0x34503b, blocker: false, allowKitbash: false });
    addAtCell(c + 3, this.rows - 4, { w: 0.8, h: 1.1, d: 0.8, color: 0x34503b, blocker: false, allowKitbash: false });
  }

  _buildBossLights() {
    const c = Math.floor(this.cols / 2);
    const north = this._cellToWorld(c, 3);
    const sign = new THREE.Mesh(
      new THREE.PlaneGeometry(6.5, 1.6),
      new THREE.MeshStandardMaterial({ color: 0x111111, emissive: 0xff2d6b, emissiveIntensity: 2.2, side: THREE.DoubleSide }),
    );
    sign.rotation.x = Math.PI / 2;
    sign.position.set(north.x, 1.92, north.z);
    this.group.add(sign);
    this.group.add(new THREE.PointLight(0xff2d6b, 2.2, 10, 2).position.copy(new THREE.Vector3(north.x, 1.8, north.z)));

    // Runner red accents
    const zStart = this._cellToWorld(c, 5).z;
    const zEnd = this._cellToWorld(c, this.rows - 6).z;
    for (let i = 0; i < 5; i++) {
      const t = i / 4;
      const z = THREE.MathUtils.lerp(zStart, zEnd, t);
      const p = new THREE.PointLight(0xff2d6b, 0.6, 4, 2);
      p.position.set(0, 0.7, z);
      this.group.add(p);
    }

    // Artwork glow lights along walls
    for (const [idx, row] of [10, 16].entries()) {
      const left = this._cellToWorld(2, row);
      const right = this._cellToWorld(this.cols - 3, row);
      const colorA = 0xff2d6b;
      const colorB = 0xff2d6b;
      const l1 = new THREE.PointLight(colorA, 0.5, 3, 2);
      l1.position.set(left.x + 0.8, 1.1, left.z);
      this.group.add(l1);
      const l2 = new THREE.PointLight(colorB, 0.5, 3, 2);
      l2.position.set(right.x - 0.8, 1.1, right.z);
      this.group.add(l2);
    }

    // Corner accent lights
    const corners = [
      this._cellToWorld(1, 1),
      this._cellToWorld(this.cols - 2, 1),
      this._cellToWorld(1, this.rows - 2),
      this._cellToWorld(this.cols - 2, this.rows - 2),
    ];
    for (const cc of corners) {
      const l = new THREE.PointLight(0xff2d6b, 0.4, 5, 2);
      l.position.set(cc.x, 1.2, cc.z);
      this.group.add(l);
    }
    this.group.add(new THREE.AmbientLight(0x080808, 0.08));
  }

  _buildBossLaserAccent() {
    const c = Math.floor(this.cols / 2);
    const z = this._cellToWorld(c, this.rows - 6).z;
    const laserMat = new THREE.MeshStandardMaterial({ color: 0x111111, emissive: 0xff2d6b, emissiveIntensity: 1.7 });
    for (let i = 0; i < 2; i++) {
      const m = new THREE.Mesh(new THREE.BoxGeometry(6, 0.05, 0.15), laserMat);
      m.position.set(0, 0.03, z + i * 0.8);
      this.group.add(m);
    }
  }

  _lockBossEntranceVisual() {
    if (!this.door) return;
    const darkMat = new THREE.MeshStandardMaterial({ color: 0x111111, emissive: 0xff2d6b, emissiveIntensity: 0.1 });
    const mesh = new THREE.Mesh(new THREE.BoxGeometry(CELL_SIZE * 1.2, 1.6, 0.2), darkMat);
    mesh.position.set(this.door.x, 0.8, this.door.z + 0.35);
    mesh.castShadow = true;
    this.group.add(mesh);
    this.door.mesh = mesh;
    this.door.material = darkMat;
    this.door.blockerWall = {
      minX: this.door.x - DOOR_BLOCKER_HALF_WIDTH,
      maxX: this.door.x + DOOR_BLOCKER_HALF_WIDTH,
      minZ: this.door.z - DOOR_BLOCKER_HALF_DEPTH,
      maxZ: this.door.z + DOOR_BLOCKER_HALF_DEPTH,
    };
    this.walls.push(this.door.blockerWall);
  }

  getAtmosphereBounds() {
    const halfX = (this.cols * CELL_SIZE) / 2;
    const halfZ = (this.rows * CELL_SIZE) / 2;
    return { minX: -halfX, maxX: halfX, minZ: -halfZ, maxZ: halfZ };
  }

  unlockDoor() {
    if (!this.door || this.doorOpen) return;
    this.doorOpen = true;
    if (this.door.material?.emissive) {
      this.door.material.color.setHex(0x111111);
      this.door.material.emissive.setHex(0xff2d6b);
      this.door.material.emissiveIntensity = 0.12;
    }
    if (this.door.mesh) this.door.mesh.visible = false;
    const idx = this.walls.indexOf(this.door.blockerWall);
    if (idx !== -1) this.walls.splice(idx, 1);
    this._navRevision++;

    // ── Exit door glow indicator ──────────────────────────────────────
    const glowMat = new THREE.MeshStandardMaterial({
      color: 0xff2d6b,
      emissive: 0xff2d6b,
      emissiveIntensity: 2.8,
      roughness: 0.4,
      metalness: 0.1,
    });
    // Floor glow tile
    const glowTile = new THREE.Mesh(
      new THREE.BoxGeometry(CELL_SIZE * 0.85, 0.06, CELL_SIZE * 0.85),
      glowMat,
    );
    glowTile.position.set(this.door.x, 0.04, this.door.z);
    this.group.add(glowTile);
    // Floating arrow chevron pointing toward door
    const arrowMat = new THREE.MeshStandardMaterial({
      color: 0xff2d6b,
      emissive: 0xff2d6b,
      emissiveIntensity: 3.0,
    });
    const arrowMesh = new THREE.Mesh(
      new THREE.BoxGeometry(CELL_SIZE * 0.5, 0.08, CELL_SIZE * 0.25),
      arrowMat,
    );
    arrowMesh.position.set(this.door.x, 0.55, this.door.z - CELL_SIZE * 0.7);
    arrowMesh.rotation.y = Math.PI * 0.08; // slight angle
    this.group.add(arrowMesh);
    // Point light
    const doorLight = new THREE.PointLight(0xff2d6b, 3.5, 8, 2);
    doorLight.position.set(this.door.x, 0.8, this.door.z);
    this.group.add(doorLight);

    this._doorGlow = { tile: glowTile, arrow: arrowMesh, light: doorLight, mat: glowMat, t: 0 };
  }

  breachAt(x, z, radius = 1.2) {
    let removed = 0;
    const r2 = radius * radius;
    for (let i = this.walls.length - 1; i >= 0; i--) {
      const w = this.walls[i];
      if (w === this.door?.blockerWall) continue;
      const cx = (w.minX + w.maxX) * 0.5;
      const cz = (w.minZ + w.maxZ) * 0.5;
      const dx = cx - x;
      const dz = cz - z;
      if (dx * dx + dz * dz <= r2) {
        this.walls.splice(i, 1);
        removed++;
      }
    }
    if (removed > 0) this._navRevision++;
    return removed > 0;
  }

  playerAtDoor(px, pz) {
    if (!this.door || !this.doorOpen) return false;
    const dx = px - this.door.x;
    const dz = pz - this.door.z;
    return Math.abs(dx) < CELL_SIZE * 0.8 && Math.abs(dz) < CELL_SIZE * 0.8;
  }

  dispose() {
    this._isDisposed = true;
    this.scene.remove(this.group);
    this.group.traverse((c) => {
      if (c.geometry) c.geometry.dispose?.();
    });
  }
}
